import { useCallback, useEffect, useRef, useState } from 'react'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { ConfigOptionIcon } from '../shared/config-option-icon'
import { getAccessTokenFromStorage } from '../../offline/session-store'
import {
  deleteConfigIcon,
  fetchConfigIconUsage,
  getApiErrorMessage,
  isApiSuccess,
  parseConfigIconUsage,
  parseRenameFilename,
  parseUploadFilename,
  renameConfigIcon,
  uploadConfigIcon,
} from '../../services/config-icons-api'
import {
  clearConfigOptionIconManifestCache,
  DEFAULT_OPTION_ICON_FILENAME,
  fetchConfigOptionIconManifest,
  isDefaultConfigIcon,
  isValidSymbolicIconName,
  normalizeSymbolicIconName,
  normalizeStoredOptionIcon,
  sanitizeIconFilename,
  symbolicNameToFilename,
} from '../../utils/config-option-icons'

/**
 * Modal grid to pick, upload, rename or delete config option SVG icons.
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string | null | undefined} props.value
 * @param {(filename: string | null) => void} props.onSelect
 * @param {string} [props.optionLabel]
 * @param {(renamedFrom: string, renamedTo: string) => void} [props.onIconRenamed]
 * @param {(deletedFilename: string) => void} [props.onIconDeleted]
 */
export default function OptionIconPickerDialog({
  open,
  onClose,
  value,
  onSelect,
  optionLabel = '',
  onIconRenamed,
  onIconDeleted,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const fileInputRef = useRef(/** @type {HTMLInputElement|null} */(null))

  const [icons, setIcons] = useState(/** @type {string[]} */([]))
  const [loading, setLoading] = useState(false)
  const [actionError, setActionError] = useState(/** @type {string|null} */(null))
  const [uploadName, setUploadName] = useState('')
  const [uploadFile, setUploadFile] = useState(/** @type {File|null} */(null))
  const [uploadBusy, setUploadBusy] = useState(false)

  const [renameTarget, setRenameTarget] = useState(/** @type {string|null} */(null))
  const [renameName, setRenameName] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState(/** @type {string|null} */(null))
  const [deleteUsageCount, setDeleteUsageCount] = useState(0)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteLoadingUsage, setDeleteLoadingUsage] = useState(false)

  const titleColor = theme.palette.newColors?.gray || theme.palette.text.primary
  const muted = theme.palette.text.secondary

  const loadIcons = useCallback(async () => {
    setLoading(true)
    setActionError(null)
    try {
      clearConfigOptionIconManifestCache()
      const token = getAccessTokenFromStorage() || ''
      const list = await fetchConfigOptionIconManifest(token)
      setIcons(list)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : _(msg`Failed to load icons.`))
    } finally {
      setLoading(false)
    }
  }, [_])

  useEffect(() => {
    if (!open) return
    setUploadName('')
    setUploadFile(null)
    setRenameTarget(null)
    setDeleteTarget(null)
    setActionError(null)
    loadIcons()
  }, [open, loadIcons])

  const handleSelect = (filename) => {
    onSelect(normalizeStoredOptionIcon(filename))
    onClose()
  }

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null
    setUploadFile(file)
    setActionError(null)
    if (file) {
      const base = file.name.replace(/\.svg$/i, '')
      setUploadName(normalizeSymbolicIconName(base))
    }
    event.target.value = ''
  }

  const handleUpload = async () => {
    const token = getAccessTokenFromStorage()
    if (!token) {
      setActionError(_(msg`Session expired. Please sign in again.`))
      return
    }
    if (!uploadFile) {
      setActionError(_(msg`Select an SVG file to upload.`))
      return
    }
    if (!isValidSymbolicIconName(uploadName)) {
      setActionError(
        _(msg`Icon name must be lowercase letters and numbers, with optional words separated by hyphens (e.g. my-vehicle).`)
      )
      return
    }

    const targetFilename = symbolicNameToFilename(uploadName)
    if (targetFilename && icons.includes(targetFilename)) {
      setActionError(_(msg`An icon with this name already exists.`))
      return
    }

    setUploadBusy(true)
    setActionError(null)
    try {
      const { status, body } = await uploadConfigIcon(token, uploadFile, uploadName)
      if (status < 200 || status >= 300 || !isApiSuccess(body)) {
        throw new Error(getApiErrorMessage(body))
      }
      const filename = parseUploadFilename(body) || targetFilename
      clearConfigOptionIconManifestCache()
      await loadIcons()
      setUploadFile(null)
      setUploadName('')
      if (filename) handleSelect(filename)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : _(msg`Upload failed.`))
    } finally {
      setUploadBusy(false)
    }
  }

  const openRenameDialog = (filename) => {
    setRenameTarget(filename)
    setRenameName(filename.replace(/\.svg$/i, ''))
    setActionError(null)
  }

  const handleRename = async () => {
    const token = getAccessTokenFromStorage()
    if (!token || !renameTarget) return

    if (!isValidSymbolicIconName(renameName)) {
      setActionError(
        _(msg`Icon name must be lowercase letters and numbers, with optional words separated by hyphens (e.g. my-vehicle).`)
      )
      return
    }

    const newFilename = symbolicNameToFilename(renameName)
    if (newFilename && newFilename !== renameTarget && icons.includes(newFilename)) {
      setActionError(_(msg`An icon with this name already exists.`))
      return
    }

    setRenameBusy(true)
    setActionError(null)
    try {
      const { status, body } = await renameConfigIcon(token, renameTarget, renameName)
      if (status < 200 || status >= 300 || !isApiSuccess(body)) {
        throw new Error(getApiErrorMessage(body))
      }
      const renamedTo = parseRenameFilename(body) || newFilename
      clearConfigOptionIconManifestCache()
      await loadIcons()
      if (renamedTo && sanitizeIconFilename(value) === renameTarget) {
        onSelect(renamedTo)
      }
      onIconRenamed?.(renameTarget, renamedTo || renameTarget)
      setRenameTarget(null)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : _(msg`Rename failed.`))
    } finally {
      setRenameBusy(false)
    }
  }

  const openDeleteDialog = async (filename) => {
    setDeleteTarget(filename)
    setDeleteUsageCount(0)
    setActionError(null)

    const token = getAccessTokenFromStorage()
    if (!token) return

    setDeleteLoadingUsage(true)
    try {
      const { status, body } = await fetchConfigIconUsage(token, filename)
      if (status >= 200 && status < 300 && isApiSuccess(body)) {
        setDeleteUsageCount(parseConfigIconUsage(body))
      }
    } catch {
      /* usage hint is optional */
    } finally {
      setDeleteLoadingUsage(false)
    }
  }

  const handleDelete = async () => {
    const token = getAccessTokenFromStorage()
    if (!token || !deleteTarget) return

    setDeleteBusy(true)
    setActionError(null)
    try {
      const { status, body } = await deleteConfigIcon(token, deleteTarget)
      if (status < 200 || status >= 300 || !isApiSuccess(body)) {
        throw new Error(getApiErrorMessage(body))
      }
      clearConfigOptionIconManifestCache()
      if (sanitizeIconFilename(value) === deleteTarget) {
        onSelect(DEFAULT_OPTION_ICON_FILENAME)
      }
      onIconDeleted?.(deleteTarget)
      setDeleteTarget(null)
      await loadIcons()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : _(msg`Delete failed.`))
    } finally {
      setDeleteBusy(false)
    }
  }

  const previewFilename = symbolicNameToFilename(uploadName)

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: titleColor, fontWeight: 700, pr: 6 }}>
          <Trans>Choose icon</Trans>
          {optionLabel ? (
            <Typography variant="body2" sx={{ color: muted, fontWeight: 400, mt: 0.5 }}>
              {optionLabel}
            </Typography>
          ) : null}
          <IconButton
            aria-label={_(msg`Close`)}
            onClick={onClose}
            sx={{ position: 'absolute', right: 12, top: 12, color: titleColor }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 200 }}>
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: titleColor, fontWeight: 600 }}>
              <Trans>Upload SVG</Trans>
            </Typography>
            <Typography variant="caption" sx={{ color: muted, display: 'block', mt: -1 }}>
              <Trans>
                Use a lowercase symbolic name; separate words with hyphens (e.g. heavy-truck).
              </Trans>
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{ flexShrink: 0 }}
              >
                <Trans>Select file</Trans>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/svg+xml,.svg"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              <TextField
                size="small"
                fullWidth
                label={_(msg`Icon name`)}
                value={uploadName}
                onChange={(e) => setUploadName(normalizeSymbolicIconName(e.target.value))}
                placeholder="my-vehicle"
                helperText={
                  previewFilename
                    ? `${_(msg`File`)}: ${previewFilename}`
                    : _(msg`Lowercase letters, numbers and hyphens only`)
                }
              />
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploadBusy || !uploadFile || !isValidSymbolicIconName(uploadName)}
                sx={{ flexShrink: 0, minWidth: 100 }}
              >
                {uploadBusy ? <CircularProgress size={20} color="inherit" /> : <Trans>Upload</Trans>}
              </Button>
            </Stack>
            {uploadFile ? (
              <Typography variant="caption" sx={{ color: muted }}>
                {uploadFile.name}
              </Typography>
            ) : null}
          </Stack>

          {actionError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          ) : null}

          {loading && (
            <Typography variant="body2" sx={{ color: muted }}>
              <Trans>Loading icons…</Trans>
            </Typography>
          )}
          {!loading && icons.length === 0 && (
            <Typography variant="body2" sx={{ color: muted }}>
              <Trans>No icons found in the configured folder.</Trans>
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
              gap: 1,
            }}
          >
            {icons.map((filename) => {
              const selected = sanitizeIconFilename(value) === filename
              const isBuiltIn = isDefaultConfigIcon(filename)
              return (
                <Box
                  key={filename}
                  sx={{
                    position: 'relative',
                    border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: selected ? 'action.selected' : 'background.paper',
                  }}
                >
                  <Stack direction="row" justifyContent="flex-end" spacing={0.25} sx={{ minHeight: 28 }}>
                    {!isBuiltIn ? (
                      <>
                        <Tooltip title={_(msg`Rename`)}>
                          <IconButton
                            size="small"
                            aria-label={_(msg`Rename`)}
                            onClick={() => openRenameDialog(filename)}
                          >
                            <DriveFileRenameOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={_(msg`Delete`)}>
                          <IconButton
                            size="small"
                            aria-label={_(msg`Delete`)}
                            onClick={() => openDeleteDialog(filename)}
                          >
                            <DeleteOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : null}
                  </Stack>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => handleSelect(filename)}
                    sx={{
                      flexDirection: 'column',
                      py: 0.5,
                      px: 0.5,
                      minHeight: 72,
                      textTransform: 'none',
                    }}
                  >
                    <ConfigOptionIcon filename={filename} size={32} color={muted} alt={filename} />
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        lineHeight: 1.1,
                        wordBreak: 'break-all',
                        color: selected ? 'primary.main' : muted,
                      }}
                    >
                      {filename.replace(/\.svg$/i, '')}
                    </Typography>
                  </Button>
                </Box>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={{ color: muted }}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={() => handleSelect(DEFAULT_OPTION_ICON_FILENAME)} sx={{ color: muted }}>
            <Trans>Use unknown icon</Trans>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: titleColor, fontWeight: 700 }}>
          <Trans>Rename icon</Trans>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={_(msg`New name`)}
            value={renameName}
            onChange={(e) => setRenameName(normalizeSymbolicIconName(e.target.value))}
            helperText={_(msg`Lowercase letters, numbers and hyphens only`)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRenameTarget(null)} sx={{ color: muted }}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            variant="contained"
            onClick={handleRename}
            disabled={renameBusy || !isValidSymbolicIconName(renameName)}
          >
            {renameBusy ? <CircularProgress size={20} color="inherit" /> : <Trans>Rename</Trans>}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: titleColor, fontWeight: 700 }}>
          <Trans>Delete icon</Trans>
        </DialogTitle>
        <DialogContent>
          {deleteLoadingUsage ? (
            <CircularProgress size={24} sx={{ mb: 2 }} />
          ) : null}
          <DialogContentText sx={{ color: muted }}>
            {deleteUsageCount > 0 ? (
              <Trans>
                This icon is assigned to {deleteUsageCount} option(s). Deleting it will dissociate
                the icon from those options. Continue?
              </Trans>
            ) : (
              <Trans>Delete this icon permanently?</Trans>
            )}
          </DialogContentText>
          {deleteTarget ? (
            <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', color: titleColor }}>
              {deleteTarget}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: muted }}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteBusy}>
            {deleteBusy ? <CircularProgress size={20} color="inherit" /> : <Trans>Delete</Trans>}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
